import {
  Caption1,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuItemCheckbox,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  makeStyles,
  tokens,
  type MenuCheckedValueChangeData,
} from "@fluentui/react-components";
import { ChevronLeft16Regular, ChevronRight16Regular, Dismiss16Regular, Filter16Regular, Options16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { FuiButton, FuiFilterSelect, FuiSearchBox } from "@/components/fui-control";
import { FilterLabel, ListSearch, ListToolbar } from "@/components/list-toolbar";
import { knownAssignees, PRIORITY_LABEL, PRIORITY_ORDER, STATUS_LABEL, STATUS_ORDER } from "@/store/execution-task-ops";
import { DEFAULT_VISIBLE_COLUMNS, TASK_COLUMNS, type TaskColumnDef, type TaskColumnKey } from "./task-columns";
import { emptyFilter, GROUP_LABEL, isFilterActive, LIST_SORT_KEYS, scopeRef, SORT_LABEL, type DueWindow, type GroupKey, type SortKey, type TaskFilter, type TaskQueryContext } from "./task-query";
import { DUE_FILTER_OPTIONS, TaskFilterChips } from "./task-filter-chips";
import { priorityGlyph, statusGlyph, taskFieldIcon } from "./task-glyphs";

const useStyles = makeStyles({
  nowrap: {
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  /** 정렬·그룹 버튼의 이름. 값은 그 오른쪽, 진한 글자. */
  menuPrefix: {
    color: tokens.colorNeutralForeground3,
  },
  menuValue: {
    color: tokens.colorNeutralForeground1,
  },
  /** 표시 버튼에 적는 현재 정렬. */
  sortValue: {
    maxWidth: "88px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
  },
  /** 도구 클러스터와 주요 액션 사이의 1px 세로 구분선 */
  vDivider: {
    width: "1px",
    height: "16px",
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
    marginLeft: "4px",
    marginRight: "2px",
  },
  popover: {
    maxHeight: "70vh",
    overflowY: "auto",
    minWidth: "240px",
    maxWidth: "320px",
  },
  hint: {
    display: "block",
    maxWidth: "120px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  /** 뒤로와 검색. 목록이 길어도 위에 남는다. */
  assigneeHead: {
    position: "sticky",
    top: "0",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: `0 2px 0 ${tokens.colorNeutralBackground1}`,
  },
  assigneeFind: {
    width: "100%",
  },
  assigneeNone: {
    display: "block",
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
  },
});

const UNASSIGNED_LABEL = "담당 미정";

type FilterSection = "status" | "priority" | "due" | "assignee" | "bucket" | "label" | "scope" | "sort" | "undecided";

const SECTION_LABEL: Record<FilterSection, string> = {
  status: "상태",
  priority: "우선순위",
  due: "마감",
  assignee: "담당",
  bucket: "버킷",
  label: "라벨",
  scope: "WBS",
  sort: "정렬",
  undecided: "미정",
};

/** 하나면 그 이름, 여러 개면 개수. 없으면 줄을 비운다. */
function selectionHint(labels: string[]): string | undefined {
  if (labels.length === 0) return undefined;
  if (labels.length === 1) return labels[0];
  return String(labels.length);
}

/** 담당 메뉴. 검색이 비어 있으면 사람 전부와 「담당 미정」을 보여 준다. */
export function visibleAssignees(names: string[], query: string): { names: string[]; unassigned: boolean } {
  const q = query.trim().toLowerCase();
  if (!q) return { names, unassigned: true };
  return {
    names: names.filter((name) => name.toLowerCase().includes(q)),
    unassigned: UNASSIGNED_LABEL.toLowerCase().includes(q),
  };
}

/** 받침이 있으면 「으로」, 없거나 ㄹ이면 「로」. */
function backLabel(title: string): string {
  const last = title.charCodeAt(title.length - 1);
  const syllable = last - 0xac00;
  const hangul = syllable >= 0 && syllable <= 11171;
  const batchim = hangul ? syllable % 28 : 0;
  const particle = batchim !== 0 && batchim !== 8 ? "으로" : "로";
  return `${title}${particle} 돌아가기`;
}

export interface ColumnControlProps {
  visible: TaskColumnKey[];
  onChange: (keys: TaskColumnKey[]) => void;
  options?: TaskColumnDef[];
}

interface Props {
  filter: TaskFilter;
  onChange: (next: TaskFilter) => void;
  ctx: TaskQueryContext;
  group?: { value: GroupKey; onChange: (g: GroupKey) => void; options?: GroupKey[]; labelPrefix?: string };
  sort?: { value: SortKey; onChange: (s: SortKey) => void };
  columns?: ColumnControlProps;
  /** assignee suggestions override (cross-project lists) */
  assignees?: string[];
  /** hide the scope filter (cross-project lists) */
  noScope?: boolean;
  /** false면 검색·필터를 숨긴다. 화면 보기와 표시는 남긴다. */
  showFilters?: boolean;
  /** false면 목록 검색만 숨긴다. 크롬 검색이 그 자리를 대신할 때. */
  showSearch?: boolean;
  /** 제목 줄. 필터 줄은 그 아래다. */
  title?: string;
  /** 제목 아래 한 줄. 플랜 수처럼 제목이 말하지 않는 범위만 적는다. */
  description?: string;
  /** 제목 줄 오른쪽의 본동사. 상단 바에는 `inHeader`로 숨긴다. */
  headingAction?: ReactNode;
  /** 선택 일괄 편집. 있으면 화면 보기·필터·검색·표시·본동사는 숨긴다. */
  leading?: ReactNode;
  /** 화면을 가르는 보기. 필터 줄 오른쪽. 선택 중에는 숨긴다. */
  view?: ReactNode;
  /** 제목 아래 밑줄 탭. 목록·보드·분석처럼 본문이 갈릴 때. */
  sections?: ReactNode;
  /** 같은 표의 범위. 검색 오른쪽. 선택 중에는 숨긴다. */
  scope?: ReactNode;
  /** 상태가 세그먼트일 때. 필터 메뉴·칩·개수에서 빼고, 초기화해도 그 범위는 남긴다. */
  statusInScope?: boolean;
  /** 본동사는 `usePageAction`을 쓴다. 선택 중에는 숨기는 화면 전용 요소만 여기에 둔다. */
  action?: ReactNode;
  /** 보드 구성. 표시 메뉴가 있으면 맨 아래 항목이고, 없으면 글자 버튼이다. */
  displayAction?: { label: string; onClick: () => void };
  children?: ReactNode;
}

export function TaskFilterBar({ filter, onChange, ctx, group, sort, columns, assignees, noScope, showFilters = true, showSearch = true, title, description, headingAction, leading, view, sections: pageSections, scope, statusInScope, action, displayAction, children }: Props) {
  const styles = useStyles();
  const [assigneeQuery, setAssigneeQuery] = useState("");
  const [section, setSection] = useState<FilterSection | null>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const rootRefs = useRef(new Map<FilterSection, HTMLDivElement>());
  const returnFocus = useRef<FilterSection | null>(null);
  const names = useMemo(() => assignees ?? knownAssignees({ tasks: ctx.project.tasks, executionTasks: [] }), [assignees, ctx.project.tasks]);
  const allNames = useMemo(() => {
    const set = new Set(names);
    for (const a of filter.assignees) if (a) set.add(a);
    return [...set];
  }, [names, filter.assignees]);
  const menuFilter = statusInScope ? { ...filter, statuses: [] } : filter;
  const active = isFilterActive(menuFilter);
  const resetFilters = () => {
    const cleared = emptyFilter();
    onChange(statusInScope ? { ...cleared, statuses: filter.statuses, hideDone: filter.hideDone } : cleared);
  };
  const set = <K extends keyof TaskFilter>(key: K, value: TaskFilter[K]) => onChange({ ...filter, [key]: value });
  const checked = {
    status: filter.statuses,
    priority: filter.priorities,
    assignee: filter.assignees.map((a) => (a === "" ? "__none" : a)),
    bucket: filter.bucketIds.map((b) => (b === "" ? "__none" : b)),
    label: filter.labelIds,
    due: [filter.due],
    scope: filter.linkedTaskIds,
    flag: [
      ...(filter.hideDone ? ["hideDone"] : []),
      ...(filter.undecidedOnly ? ["undecidedOnly"] : []),
    ],
    ...(sort ? { sort: [sort.value] } : {}),
  };
  const onChecked = (_: unknown, d: MenuCheckedValueChangeData) => {
    switch (d.name) {
      case "status":
        set("statuses", d.checkedItems as TaskFilter["statuses"]);
        break;
      case "priority":
        set("priorities", d.checkedItems as TaskFilter["priorities"]);
        break;
      case "assignee":
        set("assignees", d.checkedItems.map((a) => (a === "__none" ? "" : a)));
        break;
      case "bucket":
        set("bucketIds", d.checkedItems.map((b) => (b === "__none" ? "" : b)));
        break;
      case "label":
        set("labelIds", d.checkedItems);
        break;
      case "due":
        set("due", (d.checkedItems[0] as DueWindow) ?? "any");
        break;
      case "scope":
        set("linkedTaskIds", d.checkedItems);
        break;
      case "flag":
        onChange({
          ...filter,
          hideDone: d.checkedItems.includes("hideDone"),
          undecidedOnly: d.checkedItems.includes("undecidedOnly"),
        });
        break;
      case "sort":
        if (sort) sort.onChange((d.checkedItems[0] as SortKey) ?? sort.value);
        break;
    }
  };
  const assigneeMenu = useMemo(() => visibleAssignees(allNames, assigneeQuery), [allNames, assigneeQuery]);
  const activeScopes = useMemo(
    () => (noScope ? [] : filter.linkedTaskIds.map((id) => scopeRef(id, ctx))),
    [noScope, filter.linkedTaskIds, ctx],
  );
  const dueLabel = DUE_FILTER_OPTIONS.find((o) => o.value === filter.due)?.label;
  const hints: Partial<Record<FilterSection, string>> = {
    status: selectionHint(filter.statuses.map((s) => (s === "unset" ? "상태 미정" : STATUS_LABEL[s]))),
    priority: selectionHint(filter.priorities.map((p) => (p === "unset" ? "우선순위 미정" : PRIORITY_LABEL[p]))),
    due: filter.due === "any" ? undefined : dueLabel,
    assignee: selectionHint(filter.assignees.map((a) => (a === "" ? UNASSIGNED_LABEL : a))),
    bucket: selectionHint(filter.bucketIds.map((id) => (id === "" ? "버킷 없음" : (ctx.project.planner.buckets.find((b) => b.id === id)?.name ?? id)))),
    label: selectionHint(filter.labelIds.map((id) => ctx.project.planner.labels.find((l) => l.id === id)?.name ?? id)),
    scope: selectionHint(activeScopes.map((r) => `${r.wbs} ${r.name}`.trim())),
  };
  const sections: FilterSection[] = [
    ...(statusInScope ? [] : (["status"] as const)),
    "priority",
    "due",
    "assignee",
    ...(ctx.project.planner.buckets.length > 0 ? (["bucket"] as const) : []),
    ...(ctx.project.planner.labels.length > 0 ? (["label"] as const) : []),
    ...(activeScopes.length > 0 ? (["scope"] as const) : []),
  ];
  const menuSections: FilterSection[] = [...(sort ? (["sort"] as const) : []), "undecided", ...sections];
  const sectionHint = (id: FilterSection): string | undefined => {
    if (id === "sort") return sort ? SORT_LABEL[sort.value] : undefined;
    if (id === "undecided") return filter.undecidedOnly ? "필드만" : undefined;
    return hints[id];
  };
  const openSection = (next: FilterSection) => setSection(next);
  const goBack = () => {
    returnFocus.current = section;
    setAssigneeQuery("");
    setSection(null);
  };
  useEffect(() => {
    if (section) {
      backRef.current?.focus();
      return;
    }
    const from = returnFocus.current;
    if (!from) return;
    returnFocus.current = null;
    rootRefs.current.get(from)?.focus();
  }, [section]);
  const allColumns = columns?.options ?? TASK_COLUMNS;

  // Count active filter conditions for the badge
  const filterCount = useMemo(() => {
    let cnt = 0;
    if (!statusInScope && filter.statuses.length) cnt += filter.statuses.length;
    if (filter.priorities.length) cnt += filter.priorities.length;
    if (filter.assignees.length) cnt += filter.assignees.length;
    if (filter.bucketIds.length) cnt += filter.bucketIds.length;
    if (filter.labelIds.length) cnt += filter.labelIds.length;
    if (filter.linkedTaskIds.length) cnt += filter.linkedTaskIds.length;
    if (filter.due !== "any") cnt += 1;
    if (filter.undecidedOnly) cnt += 1;
    return cnt;
  }, [filter, statusInScope]);

  const selecting = Boolean(leading);
  const showView = !selecting && view != null;
  const showFilterMenu = showFilters && !selecting;
  const hasViewOptions = Boolean(group || sort || columns);
  const showDisplayMenu = !selecting && hasViewOptions;
  const showChildren = !selecting && children != null;
  const backItem = section ? (
    <MenuItem ref={backRef} persistOnClick icon={<ChevronLeft16Regular />} aria-label={backLabel(SECTION_LABEL[section])} onClick={goBack}>
      {SECTION_LABEL[section]}
    </MenuItem>
  ) : null;

  const filterMenuNode = showFilterMenu ? (
        <Menu
          checkedValues={checked}
          onCheckedValueChange={onChecked}
          onOpenChange={(_, data) => {
            if (!data.open) {
              setAssigneeQuery("");
              setSection(null);
            }
          }}
        >
          <MenuTrigger disableButtonEnhancement>
            <FuiButton className={styles.nowrap} icon={<Filter16Regular />} appearance="subtle" aria-label={filterCount > 0 ? `필터: ${filterCount}` : "필터"}>
              <FilterLabel count={filterCount} />
            </FuiButton>
          </MenuTrigger>
          <MenuPopover
            className={styles.popover}
            onKeyDown={(e) => {
              if (e.key !== "ArrowLeft" || !section) return;
              e.preventDefault();
              e.stopPropagation();
              goBack();
            }}
          >
            <MenuList>
              {section == null ? (
                <>
                  {statusInScope ? null : (
                    <>
                      <MenuItemCheckbox name="flag" value="hideDone" icon={taskFieldIcon("hideDone")}>
                        완료된 항목 숨기기
                      </MenuItemCheckbox>
                      <MenuDivider />
                    </>
                  )}
                  {menuSections.map((id) => {
                    const hint = sectionHint(id);
                    return (
                    <MenuItem
                      key={id}
                      persistOnClick
                      ref={(node) => {
                        if (node) rootRefs.current.set(id, node);
                        else rootRefs.current.delete(id);
                      }}
                      aria-label={hint ? `${SECTION_LABEL[id]}, ${hint}` : SECTION_LABEL[id]}
                      secondaryContent={hint ? <span className={styles.hint} title={hint}>{hint}</span> : undefined}
                      submenuIndicator={<ChevronRight16Regular />}
                      icon={taskFieldIcon(id)}
                      onClick={() => openSection(id)}
                      onKeyDown={(e) => {
                        if (e.key !== "ArrowRight") return;
                        e.preventDefault();
                        e.stopPropagation();
                        openSection(id);
                      }}
                    >
                      {SECTION_LABEL[id]}
                    </MenuItem>
                    );
                  })}
                  {active && (
                    <>
                      <MenuDivider />
                      <MenuItem icon={<Dismiss16Regular />} onClick={resetFilters}>
                        필터 초기화
                      </MenuItem>
                    </>
                  )}
                </>
              ) : section === "assignee" ? (
                <>
                  <div className={styles.assigneeHead}>
                    {backItem}
                    <div
                      className={styles.assigneeFind}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Escape" || e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") return;
                        e.stopPropagation();
                      }}
                    >
                      <FuiSearchBox
                        placeholder="이름 검색"
                        value={assigneeQuery}
                        onChange={(_, d) => setAssigneeQuery(d.value)}
                        aria-label="담당 이름"
                      />
                    </div>
                  </div>
                  {assigneeMenu.names.map((a) => (
                    <MenuItemCheckbox key={a} name="assignee" value={a} icon={taskFieldIcon("assignee")}>
                      {a}
                    </MenuItemCheckbox>
                  ))}
                  {assigneeMenu.unassigned && (
                    <MenuItemCheckbox name="assignee" value="__none" icon={taskFieldIcon("assignee")}>
                      {UNASSIGNED_LABEL}
                    </MenuItemCheckbox>
                  )}
                  {assigneeQuery.trim() && assigneeMenu.names.length === 0 && !assigneeMenu.unassigned && (
                    <Caption1 className={styles.assigneeNone}>해당하는 이름이 없습니다</Caption1>
                  )}
                </>
              ) : (
                <>
                  {backItem}
                  <MenuDivider />
                  {section === "sort" &&
                    sort &&
                    LIST_SORT_KEYS.map((key) => (
                      <MenuItemRadio key={key} name="sort" value={key}>
                        {SORT_LABEL[key]}
                      </MenuItemRadio>
                    ))}
                  {section === "undecided" && (
                    <MenuItemCheckbox name="flag" value="undecidedOnly" icon={taskFieldIcon("undecided")}>
                      미정 필드만
                    </MenuItemCheckbox>
                  )}
                  {section === "status" && (
                    <>
                      <MenuItemCheckbox name="status" value="unset" icon={statusGlyph(undefined, 16, true)}>
                        상태 미정
                      </MenuItemCheckbox>
                      {STATUS_ORDER.map((s) => (
                        <MenuItemCheckbox key={s} name="status" value={s} icon={statusGlyph(s, 16, true)}>
                          {STATUS_LABEL[s]}
                        </MenuItemCheckbox>
                      ))}
                    </>
                  )}
                  {section === "priority" && (
                    <>
                      {PRIORITY_ORDER.map((p) => (
                        <MenuItemCheckbox key={p} name="priority" value={p} icon={priorityGlyph(p)}>
                          {PRIORITY_LABEL[p]}
                        </MenuItemCheckbox>
                      ))}
                      <MenuItemCheckbox name="priority" value="unset" icon={priorityGlyph(undefined)}>
                        우선순위 미정
                      </MenuItemCheckbox>
                    </>
                  )}
                  {section === "due" &&
                    DUE_FILTER_OPTIONS.map((o) => (
                      <MenuItemRadio key={o.value} name="due" value={o.value} icon={taskFieldIcon("due")}>
                        {o.label}
                      </MenuItemRadio>
                    ))}
                  {section === "bucket" && (
                    <>
                      {ctx.project.planner.buckets.map((b) => (
                        <MenuItemCheckbox key={b.id} name="bucket" value={b.id} icon={taskFieldIcon("bucket")}>
                          {b.name}
                        </MenuItemCheckbox>
                      ))}
                      <MenuItemCheckbox name="bucket" value="__none" icon={taskFieldIcon("bucket")}>
                        버킷 없음
                      </MenuItemCheckbox>
                    </>
                  )}
                  {section === "label" &&
                    ctx.project.planner.labels.map((l) => (
                      <MenuItemCheckbox key={l.id} name="label" value={l.id} icon={taskFieldIcon("label")}>
                        {l.name}
                      </MenuItemCheckbox>
                    ))}
                  {section === "scope" &&
                    activeScopes.map((r) => (
                      <MenuItemCheckbox key={r.id} name="scope" value={r.id} icon={taskFieldIcon("scope")}>
                        {`${r.wbs} ${r.name}`.trim()}
                      </MenuItemCheckbox>
                    ))}
                </>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
  ) : null;

  const groupMenu = showDisplayMenu && group ? (
    <ChoiceMenu
      prefix={group.labelPrefix ?? "그룹"}
      value={group.value}
      icon={taskFieldIcon("group")}
      options={(group.options ?? (Object.keys(GROUP_LABEL) as GroupKey[])).map((key) => ({ value: key, label: GROUP_LABEL[key] }))}
      onChange={group.onChange}
    />
  ) : null;
  const displayMenuNode = !selecting && columns ? (
    <TaskDisplayMenu columns={columns} columnsAll={allColumns} action={displayAction} />
  ) : !selecting && displayAction ? (
    <FuiButton className={styles.nowrap} icon={<Options16Regular />} appearance="subtle" onClick={displayAction.onClick}>
      {displayAction.label}
    </FuiButton>
  ) : null;

  const searchNode = showFilterMenu && showSearch ? (
    <ListSearch value={filter.query} onChange={(query) => set("query", query)} label="Task 검색" placeholder="Task 검색" />
  ) : null;

  const hasTrailTools = Boolean(displayMenuNode);
  const hasTrail = hasTrailTools || showChildren;
  const showScope = !selecting && showFilters && scope != null;
  const keptScope = statusInScope ? { ...emptyFilter(), statuses: filter.statuses, hideDone: filter.hideDone } : undefined;

  return (
    <>
      <ListToolbar
        title={title}
        description={description}
        headingAction={selecting ? undefined : headingAction}
        sections={pageSections}
        selecting={selecting}
        search={searchNode}
        filters={
          leading || showScope || filterMenuNode || groupMenu ? (
            <>
              {leading}
              {showScope ? scope : null}
              {filterMenuNode}
              {groupMenu}
            </>
          ) : undefined
        }
        view={showView ? view : undefined}
        trail={
          hasTrail ? (
            <>
              {displayMenuNode}
              {showChildren ? children : null}
            </>
          ) : undefined
        }
        action={
          selecting || !action ? undefined : (
            <>
              {hasTrailTools && <span className={styles.vDivider} aria-hidden="true" />}
              {action}
            </>
          )
        }
      />
      {showFilterMenu && (
        <TaskFilterChips
          filter={filter}
          ctx={ctx}
          onChange={onChange}
          omit={statusInScope ? ["statuses"] : undefined}
          clearTo={keptScope}
        />
      )}
    </>
  );
}

function ChoiceMenu<T extends string>({
  prefix,
  value,
  icon,
  options,
  onChange,
}: {
  prefix: string;
  value: T;
  icon: ReactNode;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  const styles = useStyles();
  const current = options.find((option) => option.value === value)?.label ?? value;
  return (
    <Menu
      checkedValues={{ choice: [value] }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (next != null && next !== value) onChange(next as T);
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <FuiButton className={styles.nowrap} icon={icon as ReactElement} appearance="subtle">
          <span className={styles.menuPrefix}>{prefix}: </span>
          <span className={styles.menuValue}>{current}</span>
        </FuiButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList aria-label={prefix}>
          {options.map((option) => (
            <MenuItemRadio key={option.value} name="choice" value={option.value}>
              {option.label}
            </MenuItemRadio>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

type DisplaySection = "group" | "sort" | "column";

/** 정렬·그룹·열은 한 단계 들어가고, 보드 구성은 첫 화면에 둔다. */
function TaskDisplayMenu({
  group,
  sort,
  columns,
  columnsAll,
  action,
}: {
  group?: Props["group"];
  sort?: Props["sort"];
  columns?: ColumnControlProps;
  columnsAll: TaskColumnDef[];
  action?: Props["displayAction"];
}) {
  const styles = useStyles();
  const [section, setSection] = useState<DisplaySection | null>(null);
  const backRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (section) backRef.current?.focus();
  }, [section]);
  const groupOptions = group?.options ?? (Object.keys(GROUP_LABEL) as GroupKey[]);
  const groupTitle = group?.labelPrefix ?? "그룹";
  const groupValue = group ? GROUP_LABEL[group.value] : undefined;
  const sortValue = sort ? SORT_LABEL[sort.value] : undefined;
  const columnValue = columns ? `${columns.visible.length}` : undefined;
  const sectionTitle = section === "group" ? groupTitle : section === "sort" ? "정렬" : "열";
  const checked: Record<string, string[]> = {};
  if (group) checked.group = [group.value];
  if (sort) checked.sort = [sort.value];
  if (columns) checked.column = columns.visible;
  const open = (next: DisplaySection) => setSection(next);
  return (
    <Menu
      checkedValues={checked}
      onOpenChange={(_, data) => {
        if (!data.open) setSection(null);
      }}
      onCheckedValueChange={(_, d) => {
        if (d.name === "group" && group) group.onChange((d.checkedItems[0] as GroupKey) ?? group.value);
        if (d.name === "sort" && sort) sort.onChange((d.checkedItems[0] as SortKey) ?? sort.value);
        if (d.name === "column" && columns) columns.onChange(d.checkedItems as TaskColumnKey[]);
      }}
    >
      <Tooltip content={columns ? `열 ${columns.visible.length}` : "표시"} relationship="label">
        <MenuTrigger disableButtonEnhancement>
          <FuiButton className={styles.nowrap} icon={<Options16Regular />} appearance="subtle">
            {sortValue ? <span className={styles.sortValue}>{sortValue}</span> : columns ? "열" : null}
          </FuiButton>
        </MenuTrigger>
      </Tooltip>
      <MenuPopover className={styles.popover}>
        <MenuList>
          {section == null ? (
            <>
              {group && (
                <MenuItem
                  persistOnClick
                  aria-label={groupValue ? `${groupTitle}, ${groupValue}` : groupTitle}
                  secondaryContent={groupValue ? <span className={styles.hint} title={groupValue}>{groupValue}</span> : undefined}
                  submenuIndicator={<ChevronRight16Regular />}
                  icon={taskFieldIcon("group")}
                  onClick={() => open("group")}
                >
                  {groupTitle}
                </MenuItem>
              )}
              {sort && (
                <MenuItem
                  persistOnClick
                  aria-label={sortValue ? `정렬, ${sortValue}` : "정렬"}
                  secondaryContent={sortValue ? <span className={styles.hint} title={sortValue}>{sortValue}</span> : undefined}
                  submenuIndicator={<ChevronRight16Regular />}
                  icon={taskFieldIcon("sort")}
                  onClick={() => open("sort")}
                >
                  정렬
                </MenuItem>
              )}
              {columns && (
                <MenuItem
                  persistOnClick
                  aria-label={columnValue ? `열, ${columnValue}` : "열"}
                  secondaryContent={columnValue}
                  submenuIndicator={<ChevronRight16Regular />}
                  icon={taskFieldIcon("column")}
                  onClick={() => open("column")}
                >
                  열
                </MenuItem>
              )}
              {action && (
                <>
                  <MenuDivider />
                  <MenuItem onClick={action.onClick}>{action.label}</MenuItem>
                </>
              )}
            </>
          ) : (
            <>
              <MenuItem ref={backRef} persistOnClick icon={<ChevronLeft16Regular />} aria-label={backLabel(sectionTitle)} onClick={() => setSection(null)}>
                {sectionTitle}
              </MenuItem>
              <MenuDivider />
              {section === "group" &&
                groupOptions.map((g) => (
                  <MenuItemRadio key={g} name="group" value={g}>
                    {GROUP_LABEL[g]}
                  </MenuItemRadio>
                ))}
              {section === "sort" &&
                LIST_SORT_KEYS.map((k) => (
                  <MenuItemRadio key={k} name="sort" value={k}>
                    {SORT_LABEL[k]}
                  </MenuItemRadio>
                ))}
              {section === "column" && columns && (
                <>
                  <MenuGroup>
                    <MenuGroupHeader>
                      표시할 열 ({columns.visible.length}/{columnsAll.length})
                    </MenuGroupHeader>
                    {columnsAll.map((col) => (
                      <MenuItemCheckbox key={col.key} name="column" value={col.key}>
                        {col.label}
                      </MenuItemCheckbox>
                    ))}
                  </MenuGroup>
                  <MenuDivider />
                  <MenuItem persistOnClick onClick={() => columns.onChange(DEFAULT_VISIBLE_COLUMNS)}>
                    기본 열로 복원
                  </MenuItem>
                  <MenuItem persistOnClick onClick={() => columns.onChange(columnsAll.map((c) => c.key))}>
                    모든 열 표시
                  </MenuItem>
                </>
              )}
            </>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
